import React, { useState } from 'react';
import { Modal, Button, Form, Alert, InputGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import ScopeCheckboxes from '#components/shared-access/ScopeCheckboxes';
import ConsentModal from '#components/shared-access/ConsentModal';

const SharedAccessLinkCreateModal = ({ show, onHide, onCreate, isLoading }) => {
  const { t, i18n } = useTranslation();
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [expiresInDays, setExpiresInDays] = useState(1);
  const [description, setDescription] = useState('');
  const [doctorEmail, setDoctorEmail] = useState('');
  const [allowedScopes, setAllowedScopes] = useState({
    medical_documents: false,
    health_reports: false,
    health_interviews: false,
    health_summaries: false,
    biomarkers: false,
  });
  const [errors, setErrors] = useState({});

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const newErrors = {};
    const daysValue = typeof expiresInDays === 'number' ? expiresInDays : parseInt(expiresInDays, 10);
    
    if (isNaN(daysValue) || daysValue < 1 || daysValue > 7) {
      newErrors.expiresInDays = t('shared-access.errors.expires-in-days-range');
    }
    
    const emailTrimmed = doctorEmail.trim();
    if (!emailTrimmed) {
      newErrors.doctorEmail = t('shared-access.errors.doctor-email-required');
    } else if (!validateEmail(emailTrimmed)) {
      newErrors.doctorEmail = t('shared-access.errors.doctor-email-invalid');
    }
    
    const hasActiveScope = Object.values(allowedScopes).some((value) => value === true);
    if (!hasActiveScope) {
      newErrors.allowedScopes = t('shared-access.errors.at-least-one-scope');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // If consent hasn't been accepted yet, show consent modal first
    if (!consentAccepted) {
      setShowConsentModal(true);
      return;
    }

    // Filter only active scopes (only send true values)
    const activeScopes = Object.entries(allowedScopes).reduce((acc, [key, value]) => {
      if (value === true) {
        acc[key] = true;
      }
      return acc;
    }, {});

    onCreate({
      expires_in_days: typeof expiresInDays === 'number' ? expiresInDays : parseInt(expiresInDays, 10) || 1,
      allowed_scopes: activeScopes,
      description: description.trim() || undefined,
      allowed_email: doctorEmail.trim(),
      language: i18n.language || 'pl', // Send current language to backend
      consent: true, // Send consent to backend
    });
  };

  const handleConsentAccept = () => {
    setConsentAccepted(true);
    setShowConsentModal(false);
    // Continue with form submission after consent is accepted
    const activeScopes = Object.entries(allowedScopes).reduce((acc, [key, value]) => {
      if (value === true) {
        acc[key] = true;
      }
      return acc;
    }, {});

    onCreate({
      expires_in_days: typeof expiresInDays === 'number' ? expiresInDays : parseInt(expiresInDays, 10) || 1,
      allowed_scopes: activeScopes,
      description: description.trim() || undefined,
      allowed_email: doctorEmail.trim(),
      language: i18n.language || 'pl', // Send current language to backend
      consent: true, // Send consent to backend
    });
  };

  const handleClose = () => {
    setExpiresInDays(1);
    setDescription('');
    setDoctorEmail('');
    setAllowedScopes({
      medical_documents: false,
      health_reports: false,
      health_interviews: false,
      health_summaries: false,
      biomarkers: false,
    });
    setErrors({});
    setConsentAccepted(false);
    setShowConsentModal(false);
    onHide();
  };

  return (
    <>
      <ConsentModal
        show={showConsentModal}
        onHide={() => setShowConsentModal(false)}
        onAccept={handleConsentAccept}
      />
      <Modal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('shared-access.create-link')}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className='mb-3'>
              <Form.Label>{t('shared-access.expires-in-days')}</Form.Label>
              <InputGroup>
                <Button
                  variant='outline-secondary'
                  onClick={() => {
                    if (expiresInDays > 1) {
                      setExpiresInDays(expiresInDays - 1);
                    }
                  }}
                  disabled={expiresInDays <= 1}
                  style={{ minWidth: '45px' }}
                >
                  −
                </Button>
                <Form.Control
                  type='number'
                  min='1'
                  max='7'
                  value={expiresInDays}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    if (inputValue === '') {
                      setExpiresInDays(1);
                      return;
                    }
                    const value = parseInt(inputValue, 10);
                    if (!isNaN(value) && value >= 1 && value <= 7) {
                      setExpiresInDays(value);
                    }
                  }}
                  onBlur={(e) => {
                    const value = parseInt(e.target.value, 10);
                    if (isNaN(value) || value < 1) {
                      setExpiresInDays(1);
                    } else if (value > 7) {
                      setExpiresInDays(7);
                    } else {
                      setExpiresInDays(value);
                    }
                  }}
                  isInvalid={!!errors.expiresInDays}
                  style={{ textAlign: 'center' }}
                />
                <Button
                  variant='outline-secondary'
                  onClick={() => {
                    if (expiresInDays < 7) {
                      setExpiresInDays(expiresInDays + 1);
                    }
                  }}
                  disabled={expiresInDays >= 7}
                  style={{ minWidth: '45px' }}
                >
                  +
                </Button>
              </InputGroup>
              <Form.Text className='text-muted'>
                {t('shared-access.expires-in-days-help', { min: 1, max: 7 })}
              </Form.Text>
              {errors.expiresInDays && (
                <Form.Control.Feedback type='invalid'>{errors.expiresInDays}</Form.Control.Feedback>
              )}
            </Form.Group>

            <Form.Group className='mb-3'>
              <Form.Label>{t('shared-access.doctor-email')}</Form.Label>
              <Form.Control
                type='email'
                value={doctorEmail}
                onChange={(e) => {
                  setDoctorEmail(e.target.value);
                  // Clear error when user starts typing
                  if (errors.doctorEmail) {
                    const newErrors = { ...errors };
                    delete newErrors.doctorEmail;
                    setErrors(newErrors);
                  }
                }}
                placeholder={t('shared-access.doctor-email-placeholder')}
                isInvalid={!!errors.doctorEmail}
                required
              />
              <Form.Text className='text-muted'>{t('shared-access.doctor-email-help')}</Form.Text>
              {errors.doctorEmail && (
                <Form.Control.Feedback type='invalid'>{errors.doctorEmail}</Form.Control.Feedback>
              )}
            </Form.Group>

            <Form.Group className='mb-3'>
              <Form.Label>{t('shared-access.description')}</Form.Label>
              <Form.Control
                type='text'
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('shared-access.description-placeholder')}
              />
              <Form.Text className='text-muted'>{t('shared-access.description-help')}</Form.Text>
            </Form.Group>

            <Form.Group className='mb-3'>
              <Form.Label>{t('shared-access.allowed-scopes')}</Form.Label>
              {errors.allowedScopes && (
                <Alert variant='danger' className='small py-2 mb-2'>
                  {errors.allowedScopes}
                </Alert>
              )}
              <ScopeCheckboxes scopes={allowedScopes} onChange={setAllowedScopes} />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant='secondary' onClick={handleClose} disabled={isLoading}>
              {t('forms.cancel')}
            </Button>
            <Button variant='logo-green' type='submit' disabled={isLoading}>
              {isLoading ? t('forms.loading') || 'Loading...' : t('shared-access.create')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default SharedAccessLinkCreateModal;

